import { NotificationAction } from "@config/local-notifications/notification-actions";
import { NotificationIcon } from "@config/local-notifications/notification-icon";
import { organizationManagerNotificationRepo } from "@repositories";
import type { HyperEntityConfig } from "@shared/backend/hyper-entity.decorator";
import { awaiter } from "@shared/helpers/awaiter";
import { Roles } from "@shared/Roles";
import { syncOpportunityFullFlagFromSeats } from "@shared/helpers/sync-opportunity-full-flag";
import { Allow, remult, repo } from "remult";
import { GroupOpportunity } from "../group-opportunity";
import { OpportunityParticipant } from "../opportunity-participant";
import { type Group } from "./entity";

export const GroupEntityConfig: HyperEntityConfig<Group> = {
  allowApiRead: Allow.authenticated,
  allowApiCrud: [Roles.Admin, Roles.SuperAdmin, Roles.Manager, Roles.User],
  apiPrefilterPerRole: (filter) => {
    if (!remult.user) return;

    filter(Roles.SuperDuperAdmin, false);

    filter([Roles.Admin, Roles.SuperAdmin], {
      organization: {
        $id: remult.user.organizationId,
      },
    });

    filter(Roles.Manager, {
      $or: [
        {
          team: {
            $id: {
              $in: remult.user.managingTeams,
            },
          },
        },
        {
          volunteerOrganizationId: { $ne: null as unknown as string },
          organization: {
            $id: remult.user.organizationId,
          },
        },
      ],
    });

    filter(Roles.User, {
      admin: {
        $id: remult.user.id,
      },
    });
  },

  allowInsertPerRole(allow) {
    allow(Roles.User, (entity) => {
      return remult.user!.organizations!.includes(entity.organizationId);
    });

    allow(
      Roles.OrganizationManagers,
      (entity) => entity.organizationId === remult.user!.organizationId,
    );
  },

  allowDeleteAndUpdatePerRole(allow) {
    allow(Roles.User, {
      admin: {
        $id: remult.user?.id,
      },
    });

    allow([Roles.Admin, Roles.SuperAdmin], {
      organization: {
        $id: remult.user!.organizationId,
      },
    });

    allow(Roles.Manager, {
      team: {
        $id: {
          $in: remult.user!.managingTeams,
        },
      },
    });
  },

  async saving(entity) {
    if (entity.volunteerOrganizationId && entity.isDeleted) {
      throw new Error("קבוצה של ארגון מתנדבים נשמרת עם המאצ'ים שלה ולא נמחקת");
    }
  },

  async onUpdate(entity, e) {
    if (!e.fields.size.valueChanged()) return;

    const [links, participants] = await Promise.all([
      repo(GroupOpportunity).find({ where: { groupId: entity.id } }),
      repo(OpportunityParticipant).find({
        where: { groupId: entity.id, status: "APPROVED" },
      }),
    ]);
    const opportunityIds = new Set<string>();
    for (const link of links) opportunityIds.add(link.opportunityId);
    for (const participant of participants) {
      if (participant.opportunityId) opportunityIds.add(participant.opportunityId);
    }
    for (const opportunityId of opportunityIds) {
      await syncOpportunityFullFlagFromSeats({ id: opportunityId }, true);
    }
  },

  async deleting(entity) {
    if (entity.volunteerOrganizationId) {
      throw new Error("קבוצה של ארגון מתנדבים נשמרת עם המאצ'ים שלה ולא נמחקת");
    }
  },

  async onInsert(entity, e) {
    if (entity.volunteerOrganizationId) return;

    const [admin, organization] = await awaiter(
      e.relations.admin.findOne(),
      e.relations.organization.findOne({
        include: {
          managers: true,
        },
      }),
    );

    const managers = organization?.managers.filter(
      (manager) =>
        manager.roles.includes(Roles.Admin) ||
        manager.roles.includes(Roles.SuperAdmin),
    );

    if ((managers?.length || 0) > 0) {
      await organizationManagerNotificationRepo.insert(
        managers?.map((manager) => ({
          toId: manager.id,
          title: "קבוצה חדשה",
          content: `קבוצה חדשה בשם ${entity.name} נוצרה`,
          icon: NotificationIcon.GROUP,
          actions: [
            {
              btnText: "צפייה בקבוצה",
              id: NotificationAction.NAVIGATE,
              extra: {
                to: `/volunteers-groups/${entity.id}`,
              },
            },
          ],
        })) || [],
      );
    }

    if (admin && organization) {
      // SMS removed - group registration confirmation no longer sent
    }
  },
};
